import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Fade,
  Skeleton,
  Button,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/logeduser';

import { axiosInstance } from '../../api';
import PayslipPDF from '../pdf/PayslipPdf';
import PageHeader from './PageHeader';

// Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ---- Visual tokens (shared with the employee Home & Attendance dashboards) ----
// Same soft FORTHEYE-style cards used on /home so the portal feels like one app.
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';   // primary blue, matches /home
const MONEY = '#16a34a';    // green for amounts, matches the home CTC pill

const inr = (n) => Number(n || 0).toLocaleString('en-IN');

// Convert a month name ("May") to its 2-digit number ("05") so it can be fed
// to the <input type="month"> picker value ("YYYY-MM") via applyMonth().
const monthNameToNumber = (monthName) => {
  const idx = new Date(`${monthName} 1, 2000`).getMonth();
  return String(idx + 1).padStart(2, '0');
};

// Unified section heading (mirrors the home dashboard's SectionHeader).
const SectionHeader = ({ title, icon, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{title}</Typography>
    </Box>
    {action}
  </Box>
);

// Soft stat tile (replaces the old gradient summary cards).
const SummaryCard = ({ title, value, icon, color, subtitle }) => (
  <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 2, md: 2.5 }, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: '12px',
        bgcolor: `${color}14`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
      <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {title}
      </Typography>
    </Box>
    <Typography sx={{
      fontWeight: 700, color: '#111827', lineHeight: 1.1,
      fontSize: { xs: '1.25rem', md: '1.5rem' },
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {value}
    </Typography>
    {subtitle && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{subtitle}</Typography>}
  </Paper>
);

// Earnings / deductions breakdown card — soft header, dashed rows, tinted total.
const PayslipSection = ({ title, items, total, totalLabel, icon, color = ACCENT }) => (
  <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden', height: '100%' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: { xs: 2, md: 2.5 }, pb: 1.5 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: '12px',
        bgcolor: `${color}14`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.15rem' }, color: '#111827' }}>
        {title}
      </Typography>
    </Box>

    {/* Items */}
    <Box sx={{ px: { xs: 2, md: 2.5 } }}>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.25,
            borderTop: '1px dashed #eef0f4',
          }}
        >
          <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', pr: 1 }}>{item.label}</Typography>
          <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
            ₹{inr(item.value)}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* Total */}
    <Box sx={{
      mt: 1.5,
      px: { xs: 2, md: 2.5 }, py: 1.75,
      bgcolor: `${color}08`,
      borderTop: `1px solid ${color}22`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <Typography sx={{ fontWeight: 700, color, fontSize: '0.9rem' }}>{totalLabel}</Typography>
      <Typography sx={{ fontWeight: 700, color, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>₹{inr(total)}</Typography>
    </Box>
  </Paper>
);

const Payslipemp = () => {
  const { authData } = useContext(AuthContext);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [monthData, setMonthData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");

  const id = authData?.employee_no;
  const [searchParams] = useSearchParams();

  // Select a "YYYY-MM" month: set the picker and resolve the matching payslip.
  // Shared by the picker's onChange and the deep-link auto-select effect.
  const applyMonth = (value) => {
    setSelectedMonth(value);

    if (!value) {
      setMonthData(null);
      setSelectedPeriod("");
      return;
    }

    const [year, month] = value.split("-");
    const monthName = new Date(`${year}-${month}-01`).toLocaleString("default", {
      month: "long",
    });

    const period = `${monthName} ${year}`;
    setSelectedPeriod(period);

    const foundData = employee?.payslips?.find(
      (item) => item.month === monthName && item.year === parseInt(year)
    );

    if (foundData) {
      const matchingDeductions = (foundData.deduction_history || [])
        .filter(
          (entry) =>
            entry.month === monthName && entry.year === parseInt(year)
        )
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const updatedData = {
        ...foundData,
        deduction_history: (matchingDeductions && matchingDeductions.length > 0) ? [matchingDeductions[0]] : [],
      };

      setMonthData(updatedData);
    } else {
      setMonthData(null);
    }
  };

  const handleMonthChange = (event) => applyMonth(event.target.value);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!id) {
        setError("No employee ID found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(`api/my-payslips/${id}/`);
        setEmployee(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to load data");
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [id]);

  // Deep-link: a "Payslip published — May 2026" notification navigates here with
  // ?month=YYYY-MM. Once payslips are loaded, auto-select that month so the
  // payslip opens directly instead of a blank picker.
  useEffect(() => {
    const m = searchParams.get('month');
    if (m && employee?.payslips) {
      applyMonth(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, searchParams]);

  // Prepare earnings and deductions data
  const earnings = monthData ? [
    { label: 'Basic Salary', value: monthData.basic },
    { label: 'House Rent Allowance', value: monthData.hra },
    { label: 'Conveyance', value: monthData.conveyance },
    { label: 'Medical Allowance', value: monthData.medical },
    { label: 'Special Allowance', value: monthData.special_allowance },
    { label: 'Food Allowance', value: monthData.food_allowance },
    { label: 'Travel Allowance', value: monthData.travel_allowance },
    { label: 'Bonus', value: monthData.bonus },
    { label: 'Recharge', value: monthData.recharge },
    { label: 'Telephone Allowance', value: monthData.telephone_allowance },
    { label: 'Exam Increment', value: monthData.exam_increment },
    { label: 'Other Earnings', value: monthData.other_earnings },
    { label: 'Incentive', value: monthData.incentive },
    // Employer PF is shown in BOTH Earnings and Deductions on purpose: it is part
    // of the CTC-based gross and is then deducted again, so it cancels out in net
    // pay. Standard Indian payslip format - do not "fix" it as a duplicate.
    { label: 'Employer Provident Fund', value: monthData.employer_pf },
  ].filter(item => parseFloat(item.value) > 0 || ['Basic Salary', 'House Rent Allowance'].includes(item.label)) : [];

  const deductions = monthData ? [
    { label: 'Provident Fund (Employee)', value: monthData.pf },
    { label: 'Insurance', value: monthData.professional_tax },
    { label: 'Loss of Pay', value: monthData.lop_deduction },
    { label: 'Loan', value: monthData.loan },
    { label: 'Staff Advance', value: monthData.staff_advance },
    { label: 'Income Tax (TDS)', value: monthData.income_tax },
    { label: 'ESI', value: monthData.esi },
    { label: 'Penalty', value: monthData.penalty },
    { label: 'Other Deduction', value: monthData.other_deduction },
    { label: 'Employer Provident Fund', value: monthData.employer_pf_deduction || monthData.employer_pf },
  ].filter(item => parseFloat(item.value) > 0) : [];

  const netSalary = monthData
    ? parseFloat(monthData.total_earnings || 0) - parseFloat(monthData.total_deductions || 0)
    : 0;

  // Group published payslips by year (newest first) with months in true calendar
  // order. The API sorts by month *name* alphabetically (Apr, Aug, Dec...), which
  // is not chronological, so we re-sort here using the real month number.
  const payslipGroups = (() => {
    const sorted = [...(employee?.payslips || [])].sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return Number(monthNameToNumber(b.month)) - Number(monthNameToNumber(a.month));
    });
    const map = new Map();
    sorted.forEach((p) => {
      if (!map.has(p.year)) map.set(p.year, []);
      map.get(p.year).push(p);
    });
    return Array.from(map.entries());
  })();

  // Loading State
  if (loading) {
    return (
      <Box sx={{ pb: { xs: 4, md: 2 } }}>
        <LinearProgress sx={{ borderRadius: 999, mb: 2 }} />
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: '22px' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box sx={{ pb: { xs: 4, md: 2 } }}>
        <Alert severity="error" sx={{ borderRadius: '16px' }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      {/* ---- Header card (shared PageHeader) ---- */}
      <Fade in={true} timeout={500}>
        <PageHeader
          icon={<ReceiptLongIcon />}
          title="Payslip Management"
          subtitle="View and download your monthly payslips"
          action={(
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { xs: '100%', sm: 230 } }}>
              <Typography sx={{ color: '#9ca3af', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.62rem' }}>
                SELECT MONTH
              </Typography>
              <input
                type="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                style={{
                  width: '100%',
                  padding: '11px 12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '14px',
                  border: '1px solid #eef0f4',
                  backgroundColor: '#f8fafc',
                  color: '#111827',
                  outline: 'none',
                }}
              />
            </Box>
          )}
        />
      </Fade>

      {/* ---- Back to list ---- */}
      {monthData && (
        <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
          <Button
            onClick={() => applyMonth("")}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT, borderRadius: '999px', px: 1.5 }}
          >
            Back to all payslips
          </Button>
        </Box>
      )}

      {/* ---- Summary tiles ---- */}
      {monthData && (
        <Fade in={true} timeout={600}>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard title="Total Earnings" value={`₹${inr(monthData?.total_earnings)}`} icon={<TrendingUpIcon />} color={MONEY} subtitle="Gross Salary" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard title="Total Deductions" value={`₹${inr(monthData?.total_deductions)}`} icon={<TrendingDownIcon />} color="#ef4444" subtitle="Tax & PF" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard title="Net Salary" value={`₹${inr(netSalary)}`} icon={<AccountBalanceWalletIcon />} color={ACCENT} subtitle="Take Home" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard title="Payment Period" value={selectedPeriod || 'Not Selected'} icon={<CalendarMonthIcon />} color="#f59e0b" subtitle="Monthly" />
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* ---- Detail view OR default list ---- */}
      {monthData ? (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={700}>
              <Box sx={{ height: '100%' }}>
                <PayslipSection title="Earnings" items={earnings} total={monthData?.total_earnings} totalLabel="TOTAL EARNINGS" icon={<TrendingUpIcon />} color={MONEY} />
              </Box>
            </Fade>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={800}>
              <Box sx={{ height: '100%' }}>
                <PayslipSection title="Deductions" items={deductions} total={monthData?.total_deductions} totalLabel="TOTAL DEDUCTIONS" icon={<TrendingDownIcon />} color="#ef4444" />
              </Box>
            </Fade>
          </Grid>

          <Grid item xs={12}>
            <Fade in={true} timeout={900}>
              <Paper elevation={0} sx={CARD_SX}>
                <SectionHeader title="Download Payslip" icon={<ReceiptLongIcon sx={{ color: ACCENT }} />} />
                <PayslipPDF data={monthData} id={id} />
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      ) : (
        // Default: list all published payslips, grouped by year, in calendar order
        <Fade in={true} timeout={600}>
          <Paper elevation={0} sx={CARD_SX}>
            {employee?.payslips?.length > 0 ? (
              <Box>
                <SectionHeader
                  title="Your Payslips"
                  icon={<ReceiptLongIcon sx={{ color: ACCENT }} />}
                  action={(
                    <Box sx={{ px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: `${ACCENT}12`, color: ACCENT, fontSize: '0.75rem', fontWeight: 700 }}>
                      {employee.payslips.length} total
                    </Box>
                  )}
                />

                {payslipGroups.map(([year, slips]) => (
                  <Box key={year} sx={{ mb: { xs: 2.5, md: 3 }, '&:last-of-type': { mb: 0 } }}>
                    {/* Year divider */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, color: '#9ca3af', fontSize: '0.78rem', letterSpacing: 1 }}>
                        {year}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#eef0f4' }} />
                    </Box>

                    {/* Month rows (calendar order) */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {slips.map((p, i) => {
                        const net = parseFloat(p.total_earnings || 0) - parseFloat(p.total_deductions || 0);
                        const monthValue = `${p.year}-${monthNameToNumber(p.month)}`;
                        return (
                          <Box
                            key={i}
                            onClick={() => applyMonth(monthValue)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: 1.5, md: 2 },
                              p: { xs: 1.25, md: 1.5 },
                              borderRadius: '16px',
                              border: '1px solid #eef0f4',
                              bgcolor: '#fff',
                              boxShadow: '0 1px 3px rgba(17,24,39,0.04)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: ACCENT,
                                bgcolor: '#f7faff',
                                transform: 'translateX(3px)',
                              },
                            }}
                          >
                            {/* Month badge */}
                            <Box sx={{
                              width: { xs: 46, md: 50 },
                              height: { xs: 46, md: 50 },
                              flexShrink: 0,
                              borderRadius: '14px',
                              bgcolor: '#eef4ff',
                              color: ACCENT,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: { xs: '0.78rem', md: '0.85rem' },
                              letterSpacing: 0.5,
                            }}>
                              {p.month.slice(0, 3).toUpperCase()}
                            </Box>

                            {/* Label */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                {p.month} {p.year}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                Net Salary
                              </Typography>
                            </Box>

                            {/* Amount */}
                            <Typography sx={{
                              fontWeight: 700,
                              color: MONEY,
                              fontSize: { xs: '0.95rem', md: '1.1rem' },
                              whiteSpace: 'nowrap',
                            }}>
                              ₹{inr(net)}
                            </Typography>

                            <ChevronRightIcon sx={{ color: '#cbd5e1', flexShrink: 0 }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
                <CalendarMonthIcon sx={{ fontSize: { xs: 56, md: 72 }, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 0.5, color: '#374151', fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  No Payslips Available
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                  Your published payslips will appear here once they are generated
                </Typography>
              </Box>
            )}
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default Payslipemp;
